import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { billingV2Post } from '@/lib/api/clients/billing-v2';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { paymentScheduleKeys } from '@/lib/queryKeys';
import type { FireRuleResult } from '@/types/paymentSchedule';
import { describeFireOutcome } from './fireRuleOutcome';
import { invalidatePaymentScheduleEffects } from './invalidatePaymentScheduleEffects';

/**
 * Spec §5 — admin manual fire of a single rule. Useful to retry a rule
 * whose trigger event has already passed (e.g., after fixing a bad payment
 * method) or to kick off late charges.
 */
export function useFireRule(scheduleId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<FireRuleResult, { ruleId: string; jobId?: string }>({
    mutationFn: ({ ruleId }) =>
      billingV2Post<FireRuleResult, undefined>(
        billingV2Endpoints.paymentScheduleFireRule(scheduleId, ruleId),
        undefined,
      ),
    onSuccess: (result) => {
      // "Rule fired" over a DRAFT invoice is the toast QA reported as a lie.
      // Report what the fire actually did to the customer, and hand back any
      // pay link it minted rather than dropping it on the floor.
      const outcome = describeFireOutcome(result);
      const payLink = outcome.paymentLinkUrl;
      const options = {
        description: outcome.description,
        ...(payLink
          ? {
              action: {
                label: 'Copy pay link',
                onClick: () => {
                  void navigator.clipboard.writeText(payLink);
                },
              },
            }
          : {}),
      };
      if (outcome.tone === 'error') toast.error(outcome.title, options);
      else if (outcome.tone === 'warning') toast.warning(outcome.title, options);
      else toast.success(outcome.title, options);
    },
    onSettled: (result, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: paymentScheduleKeys.detail(scheduleId),
      });
      const jobId = variables.jobId ?? result?.jobId;
      if (jobId) {
        invalidatePaymentScheduleEffects(queryClient, jobId);
      }
    },
  });
}
